/*
 * <hive-hex-carousel> — infinite hexagon product carousel for the homepage rows.
 *
 * Below 990px the server-rendered card list becomes a looping carousel:
 *  - the cards follow your finger 1:1, and a flick carries momentum across several cards
 *  - it always settles with one card centred; neighbours shrink and fade
 *  - it loops forever in both directions without jumping (cards are positioned by
 *    wrapping their offset around the loop; no DOM rebuilding while you swipe)
 *  - vertical page scrolling is never blocked (touch-action: pan-y + axis lock)
 *  - tap the centre card to open it, tap a side card to bring it to the centre
 *  - arrows, dots, keyboard (← →), trackpad swipes and focus all move it
 *  - only transform + opacity change per frame, so it stays on the compositor
 * At 990px and up it steps aside and the CSS honeycomb grid takes over.
 */
(() => {
  if (customElements.get('hive-hex-carousel')) return;

  const MOBILE_QUERY = '(max-width: 989px)';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const mod = (a, n) => ((a % n) + n) % n;
  const clamp = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  // A swipe counts as sideways unless it is clearly more vertical than horizontal
  // (up to ~53 degrees from horizontal), so slightly slanted thumb swipes still move the row.
  const isHorizontal = (dx, dy) => Math.abs(dx) >= Math.abs(dy) * 0.75;

  class HiveHexCarousel extends HTMLElement {
    // Each row sets itself up only once it is within ~1000px of the viewport, so the rows
    // don't all build and measure at page load (and the swipe is ready before you reach it).
    connectedCallback() {
      this.track = this.querySelector('[data-hc-track]');
      if (!this.track) return;
      this.mq = window.matchMedia(MOBILE_QUERY);
      this.near = !('IntersectionObserver' in window);
      this.onQueryChange = () => (this.mq.matches ? this.near && this.enable() : this.disable());
      this.mq.addEventListener('change', this.onQueryChange);
      if (!this.near) {
        this.io = new IntersectionObserver(
          (entries) => {
            if (!entries.some((e) => e.isIntersecting)) return;
            this.near = true;
            this.io.disconnect();
            this.io = null;
            this.onQueryChange();
          },
          { rootMargin: '1000px 0px' }
        );
        this.io.observe(this);
      }
      this.onQueryChange();
    }

    disconnectedCallback() {
      if (this.mq) this.mq.removeEventListener('change', this.onQueryChange);
      if (this.io) {
        this.io.disconnect();
        this.io = null;
      }
      this.disable();
    }

    /* ------------------------------------------------------------ setup */

    enable() {
      if (this.enabled) return;
      this.realItems = Array.from(this.track.children).filter((li) => !li.hasAttribute('data-hc-desktop-only'));
      this.realCount = this.realItems.length;
      if (this.realCount < 2) return;

      this.enabled = true;
      this.items = this.realItems.slice();
      this.clones = [];
      this.x = 0; // position of the loop, in cards (0 = first card centred)
      this.target = 0;
      this.current = -1;
      this.interacted = false;

      this.prevBtn = this.querySelector('[data-hc-prev]');
      this.nextBtn = this.querySelector('[data-hc-next]');
      this.dotsEl = this.querySelector('[data-hc-dots]');
      this.statusEl = this.querySelector('[data-hc-status]');

      this.setAttribute('role', 'region');
      this.setAttribute('aria-roledescription', 'carousel');
      this.track.tabIndex = 0;

      this.classList.add('is-ready');
      this.buildDots();
      this.measure();
      this.bind();
      this.render(true);

      if (this.prevBtn) this.prevBtn.hidden = false;
      if (this.nextBtn) this.nextBtn.hidden = false;
    }

    disable() {
      if (!this.enabled) return;
      this.enabled = false;
      this.stopAnimation();
      this.unbind();

      this.clones.forEach((li) => li.remove());
      this.clones = [];
      this.realItems.forEach((li) => {
        li.style.transform = '';
        li.style.opacity = '';
        li.style.zIndex = '';
        li.style.pointerEvents = '';
      });

      this.classList.remove('is-ready');
      this.removeAttribute('role');
      this.removeAttribute('aria-roledescription');
      this.track.removeAttribute('tabindex');
      this.track.classList.remove('is-dragging');
      if (this.prevBtn) this.prevBtn.hidden = true;
      if (this.nextBtn) this.nextBtn.hidden = true;
      if (this.dotsEl) {
        this.dotsEl.hidden = true;
        this.dotsEl.replaceChildren();
      }
    }

    /* Card width and spacing come from the CSS, so the look is tuned in one place. */
    measure() {
      const cardWidth = this.realItems[0].offsetWidth || 240;
      const space = parseFloat(getComputedStyle(this).getPropertyValue('--hc-space')) || 60;
      const viewport = this.track.clientWidth || window.innerWidth;
      this.cardWidth = cardWidth;
      this.step = cardWidth + space; // card + the gap between cards

      // A loop needs enough cards to cover the screen plus one on each side,
      // otherwise the wrap point would be visible. Add whole copies if it is short.
      const needed = viewport + cardWidth * 3;
      while (this.items.length * this.step < needed && this.clones.length < 60) {
        for (let i = 0; i < this.realCount; i += 1) {
          const copy = this.realItems[i].cloneNode(true);
          copy.setAttribute('aria-hidden', 'true');
          copy.setAttribute('data-hc-clone', '');
          copy.querySelectorAll('a, button').forEach((el) => el.setAttribute('tabindex', '-1'));
          this.track.appendChild(copy);
          this.clones.push(copy);
          this.items.push(copy);
        }
      }
      this.count = this.items.length;
      this.visibleRange = (viewport / 2 + cardWidth) / this.step + 0.25;
    }

    buildDots() {
      if (!this.dotsEl) return;
      const frag = document.createDocumentFragment();
      for (let i = 0; i < this.realCount; i += 1) {
        const dot = document.createElement('button');
        dot.type = 'button';
        dot.className = 'hc-dot';
        dot.setAttribute('aria-label', `Show product ${i + 1} of ${this.realCount}`);
        dot.dataset.index = String(i);
        frag.appendChild(dot);
      }
      this.dotsEl.replaceChildren(frag);
      this.dots = Array.from(this.dotsEl.children);
      this.dotsEl.hidden = false;
    }

    /* ------------------------------------------------------------ events */

    bind() {
      this.handlers = {
        down: (e) => this.onPointerDown(e),
        touchstart: (e) => this.onTouchStart(e),
        touchmove: (e) => this.onTouchMove(e),
        touchend: () => {
          this.touch = null;
        },
        move: (e) => this.onPointerMove(e),
        up: (e) => this.onPointerUp(e),
        cancel: (e) => this.onPointerCancel(e),
        click: (e) => this.onClick(e),
        key: (e) => this.onKey(e),
        focus: (e) => this.onFocus(e),
        wheel: (e) => this.onWheel(e),
        dragstart: (e) => e.preventDefault(),
        prev: () => this.go(-1),
        next: () => this.go(1),
        dot: (e) => {
          const dot = e.target.closest('.hc-dot');
          if (dot) this.goToReal(Number(dot.dataset.index));
        },
      };
      const t = this.track;
      t.addEventListener('pointerdown', this.handlers.down);
      t.addEventListener('touchstart', this.handlers.touchstart, { passive: true });
      t.addEventListener('touchmove', this.handlers.touchmove, { passive: false });
      t.addEventListener('touchend', this.handlers.touchend);
      t.addEventListener('touchcancel', this.handlers.touchend);
      t.addEventListener('pointermove', this.handlers.move);
      t.addEventListener('pointerup', this.handlers.up);
      t.addEventListener('pointercancel', this.handlers.cancel);
      t.addEventListener('lostpointercapture', this.handlers.cancel);
      t.addEventListener('click', this.handlers.click, true);
      t.addEventListener('keydown', this.handlers.key);
      t.addEventListener('focusin', this.handlers.focus);
      t.addEventListener('wheel', this.handlers.wheel, { passive: false });
      t.addEventListener('dragstart', this.handlers.dragstart);
      if (this.prevBtn) this.prevBtn.addEventListener('click', this.handlers.prev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.handlers.next);
      if (this.dotsEl) this.dotsEl.addEventListener('click', this.handlers.dot);

      this.resizeObserver = new ResizeObserver(() => {
        if (!this.enabled) return;
        this.measure();
        this.render(true);
      });
      this.resizeObserver.observe(this.track);
    }

    unbind() {
      if (!this.handlers) return;
      const t = this.track;
      t.removeEventListener('pointerdown', this.handlers.down);
      t.removeEventListener('touchstart', this.handlers.touchstart);
      t.removeEventListener('touchmove', this.handlers.touchmove);
      t.removeEventListener('touchend', this.handlers.touchend);
      t.removeEventListener('touchcancel', this.handlers.touchend);
      t.removeEventListener('pointermove', this.handlers.move);
      t.removeEventListener('pointerup', this.handlers.up);
      t.removeEventListener('pointercancel', this.handlers.cancel);
      t.removeEventListener('lostpointercapture', this.handlers.cancel);
      t.removeEventListener('click', this.handlers.click, true);
      t.removeEventListener('keydown', this.handlers.key);
      t.removeEventListener('focusin', this.handlers.focus);
      t.removeEventListener('wheel', this.handlers.wheel);
      t.removeEventListener('dragstart', this.handlers.dragstart);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handlers.prev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handlers.next);
      if (this.dotsEl) this.dotsEl.removeEventListener('click', this.handlers.dot);
      if (this.resizeObserver) this.resizeObserver.disconnect();
      this.handlers = null;
    }

    onTouchStart(e) {
      if (e.touches.length !== 1) {
        this.touch = null;
        return;
      }
      this.touch = { x: e.touches[0].clientX, y: e.touches[0].clientY, lock: null };
    }

    // Decide the direction on the first real movement. Sideways: block page scrolling for
    // the rest of this touch so the row always moves. Up/down: leave it to the page.
    onTouchMove(e) {
      const s = this.touch;
      if (!s || e.touches.length !== 1) return;
      const dx = e.touches[0].clientX - s.x;
      const dy = e.touches[0].clientY - s.y;
      if (!s.lock) {
        if (Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        s.lock = isHorizontal(dx, dy) ? 'x' : 'y';
      }
      if (s.lock === 'x' && e.cancelable) e.preventDefault();
    }

    onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      this.stopAnimation();
      this.drag = {
        id: e.pointerId,
        startX: e.clientX,
        startY: e.clientY,
        startPos: this.x,
        dragging: false,
        samples: [{ t: e.timeStamp, x: e.clientX }],
      };
    }

    onPointerMove(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      const dx = e.clientX - d.startX;
      const dy = e.clientY - d.startY;

      if (!d.dragging) {
        const lock = e.pointerType === 'touch' && this.touch ? this.touch.lock : null;
        if (!lock && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;
        if (lock === 'y' || (!lock && !isHorizontal(dx, dy))) {
          // Vertical intent: let the page scroll and forget this gesture.
          this.drag = null;
          this.resume();
          return;
        }
        d.dragging = true;
        d.startX = e.clientX; // start from here so the cards don't jump by the dead zone
        d.startPos = this.x;
        try {
          this.track.setPointerCapture(e.pointerId);
        } catch (err) {
          /* pointer already released */
        }
        this.track.classList.add('is-dragging');
        this.interacted = true;
      }

      e.preventDefault();
      this.x = d.startPos - (e.clientX - d.startX) / this.step;
      d.samples.push({ t: e.timeStamp, x: e.clientX });
      while (d.samples.length > 2 && e.timeStamp - d.samples[0].t > 100) d.samples.shift();
      this.requestRender();
    }

    onPointerUp(e) {
      const d = this.drag;
      if (!d || e.pointerId !== d.id) return;
      this.drag = null;
      if (!d.dragging) {
        // A tap. If it interrupted a slide, finish that slide; the click handler decides the rest.
        this.resume();
        return;
      }
      this.track.classList.remove('is-dragging');
      this.suppressClick = true;
      setTimeout(() => {
        this.suppressClick = false;
      }, 0);

      // Release velocity in cards per millisecond (positive = moving to later cards).
      const first = d.samples[0];
      const last = d.samples[d.samples.length - 1];
      const dt = Math.max(1, last.t - first.t);
      const recent = e.timeStamp - last.t < 80;
      const velocity = recent ? -(last.x - first.x) / dt / this.step : 0;

      // Project the flick forward, then snap to the nearest card (max 4 cards per flick).
      const projected = this.x + velocity * 260;
      const base = Math.round(this.x);
      const target = clamp(Math.round(projected), base - 4, base + 4);
      const distance = Math.abs(target - this.x);

      // Start the settle at the finger's speed so there is no hitch on release.
      let duration = 320 + distance * 90;
      if (Math.abs(velocity) > 0.0004 && Math.sign(velocity) === Math.sign(target - this.x)) {
        duration = (3 * distance) / Math.abs(velocity);
      }
      this.animateTo(target, clamp(duration, 220, 720));
    }

    onPointerCancel(e) {
      const d = this.drag;
      if (!d || (e.pointerId !== undefined && e.pointerId !== d.id)) return;
      // Touch pointers start implicitly captured by the card under the finger; when the track
      // takes the capture, that card fires lostpointercapture. Only the track losing it counts.
      if (e.type === 'lostpointercapture' && (e.target !== this.track || !d.dragging)) return;
      this.drag = null;
      this.track.classList.remove('is-dragging');
      if (d.dragging) this.animateTo(Math.round(this.x), 320);
    }

    onClick(e) {
      if (this.suppressClick) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      const li = e.target.closest('.hc-item');
      if (!li) return;
      const index = this.items.indexOf(li);
      if (index < 0) return;
      const rel = this.relative(index, this.x);
      if (Math.abs(rel) > 0.5) {
        // Side card: bring it to the centre instead of leaving the page.
        e.preventDefault();
        e.stopPropagation();
        this.interacted = true;
        this.animateTo(Math.round(this.x) + Math.round(rel));
      }
    }

    onKey(e) {
      if (e.key === 'ArrowRight') {
        e.preventDefault();
        this.go(1);
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        this.go(-1);
      }
    }

    onFocus(e) {
      if (this.drag) return;
      const li = e.target.closest('.hc-item');
      if (!li) return;
      const index = this.items.indexOf(li);
      if (index < 0) return;
      const rel = this.relative(index, this.x);
      if (Math.abs(rel) > 0.5) this.animateTo(Math.round(this.x) + Math.round(rel));
    }

    // Two-finger trackpad swipes (iPad with a trackpad, narrow desktop windows).
    onWheel(e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      e.preventDefault();
      this.stopAnimation();
      this.interacted = true;
      const unit = e.deltaMode === 1 ? 16 : 1;
      this.x += (e.deltaX * unit) / this.step;
      this.requestRender();
      clearTimeout(this.wheelTimer);
      this.wheelTimer = setTimeout(() => this.animateTo(Math.round(this.x), 280), 120);
    }

    /* ------------------------------------------------------------ movement */

    go(delta) {
      this.interacted = true;
      const from = this.animating ? this.target : Math.round(this.x);
      this.animateTo(from + delta);
    }

    goToReal(realIndex) {
      this.interacted = true;
      const currentReal = mod(Math.round(this.x), this.count) % this.realCount;
      let delta = mod(realIndex - currentReal, this.realCount);
      if (delta > this.realCount / 2) delta -= this.realCount;
      this.animateTo(Math.round(this.x) + delta);
    }

    animateTo(target, duration) {
      this.stopAnimation();
      this.target = target;
      const from = this.x;
      const distance = Math.abs(target - from);
      if (distance < 0.0005) {
        this.x = target;
        this.settle();
        return;
      }
      const time = reducedMotion.matches ? 0 : duration || clamp(340 + distance * 90, 280, 720);
      if (!time) {
        this.x = target;
        this.render();
        this.settle();
        return;
      }
      this.animating = true;
      const start = performance.now();
      const tick = (now) => {
        const p = Math.min(1, (now - start) / time);
        this.x = from + (target - from) * easeOutCubic(p);
        this.render();
        if (p < 1) {
          this.frame = requestAnimationFrame(tick);
        } else {
          this.animating = false;
          this.settle();
        }
      };
      this.frame = requestAnimationFrame(tick);
    }

    // Finish an interrupted slide (e.g. a tap or a vertical scroll started mid-animation).
    resume() {
      if (Math.abs(this.x - this.target) > 0.001) this.animateTo(this.target);
    }

    stopAnimation() {
      if (this.frame) cancelAnimationFrame(this.frame);
      if (this.renderFrame) cancelAnimationFrame(this.renderFrame);
      this.frame = null;
      this.renderFrame = null;
      this.animating = false;
    }

    // Snap to a whole card and keep the number small (the loop makes this invisible).
    settle() {
      this.x = mod(Math.round(this.x), this.count);
      this.target = this.x;
      this.render();
      this.announce();
    }

    requestRender() {
      if (this.renderFrame) return;
      this.renderFrame = requestAnimationFrame(() => {
        this.renderFrame = null;
        this.render();
      });
    }

    // Offset of card `index` from the centre, wrapped into [-count/2, count/2).
    relative(index, x) {
      const half = this.count / 2;
      return mod(index - x + half, this.count) - half;
    }

    render(force) {
      if (!this.enabled) return;
      const step = this.step;
      for (let i = 0; i < this.count; i += 1) {
        const li = this.items[i];
        const rel = this.relative(i, this.x);
        const a = Math.abs(rel);
        if (a > this.visibleRange) {
          if (li.__hcFar !== true || force) {
            li.style.opacity = '0';
            li.style.pointerEvents = 'none';
            li.style.transform = `translate3d(${(Math.sign(rel) * (this.visibleRange + 1) * step).toFixed(1)}px,0,0)`;
            li.__hcFar = true;
          }
          continue;
        }
        li.__hcFar = false;
        li.style.transform = `translate3d(${(rel * step).toFixed(2)}px,0,0)`;
        li.style.opacity = '1';
        li.style.zIndex = String(100 - Math.round(a * 10));
        li.style.pointerEvents = '';
      }

      const center = mod(Math.round(this.x), this.count) % this.realCount;
      if (center !== this.current) {
        this.current = center;
        this.updateDots();
        this.warmNeighbours();
      }
    }

    updateDots() {
      if (!this.dots) return;
      this.dots.forEach((dot, i) => dot.setAttribute('aria-current', i === this.current ? 'true' : 'false'));
    }

    // Start loading the images of the cards about to come into view. Hover images are left
    // lazy: touch screens never show them, so they must not be fetched early.
    warmNeighbours() {
      for (let i = 0; i < this.count; i += 1) {
        if (Math.abs(this.relative(i, this.x)) > 2.5) continue;
        this.items[i].querySelectorAll('img[loading="lazy"]:not(.hc-img--alt)').forEach((img) => {
          img.loading = 'eager';
        });
      }
    }

    announce() {
      if (!this.interacted || !this.statusEl) return;
      const li = this.realItems[this.current];
      const name = li ? (li.querySelector('.visually-hidden') || {}).textContent || '' : '';
      this.statusEl.textContent = `Product ${this.current + 1} of ${this.realCount}${name ? `: ${name.trim()}` : ''}`;
    }
  }

  customElements.define('hive-hex-carousel', HiveHexCarousel);
})();
